import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import httpRequest from "../../../ajax";
import axios from "axios";
import Layout from "../../../layout";
import { numberWithCommas, uploadFile } from "../../../utils/helpers";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import HistoryIcon from "@material-ui/icons/History";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import CloseIcon from "@material-ui/icons/Close";
import { CircularProgress } from "@material-ui/core";
import {
  getJobSeekerPlans,
  buyScanCredits as buyScanCreditsAction,
  getActiveCampaign as getActiveCampaignAction,
  claimCampaignCredits as claimCampaignCreditsAction,
  saveMatchScore as saveMatchScoreAction,
  getMatchScoreHistory as getMatchScoreHistoryAction,
} from "../../../redux/actions/jobSeekerSubscriptionActions";
import { getProfileAction } from "../../../redux/actions/profileActions";
import "./index.scss";

const JobMatchScore = () => {
  const [activeTab, setActiveTab] = useState("description");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [atsOpen, setAtsOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  const [buyCreditMethod, setBuyCreditMethod] = useState("wallet");
  const [buyingCredit, setBuyingCredit] = useState(false);
  const [pendingAnalyzeAfterBuy, setPendingAnalyzeAfterBuy] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [claimingCampaign, setClaimingCampaign] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [uploadingResume, setUploadingResume] = useState(false);
  const { userData } = useSelector((store) => store.auth);
  const { profileData } = useSelector((store) => store.profile || {});
  const dispatch = useDispatch();
  const history = useHistory();
  const resumeInputRef = useRef(null);
  // Temporary local toggles for UI/API testing.
  // Keep `enabled: false` in normal usage.
  const TEST_FLAGS = {
    enabled: false,
    forceIsFreeForApi: true, // true | false | null
    forceShowCampaignBanner: false, // true | false | null
    forceIsProHunter: false, // true | false | null
  };

  const refreshSubscription = async () => {
    try {
      const response = await httpRequest(
        "get",
        "/job-seeker-subscriptions/my-subscription",
      );
      if (response.data.status && response.data.data.subscription) {
        setSubscriptionData(response.data.data.subscription);
        return response.data.data.subscription;
      }
    } catch (err) {
      console.log("No active subscription or error fetching:", err);
    }
    return null;
  };

  useEffect(() => {
    const init = async () => {
      try {
        await refreshSubscription();
        await getProfileAction(dispatch);
        const plansResult = await getJobSeekerPlans(dispatch);
        if (Array.isArray(plansResult?.payload)) {
          setPlans(plansResult.payload);
        }
        // Check for active campaign
        try {
          const campaignResult = await getActiveCampaignAction(dispatch);
          if (campaignResult?.payload) {
            setCampaign(campaignResult.payload);
          }
        } catch (e) {
          // Campaign check is non-critical
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    const savedResumeUrl = profileData?.resume || userData?.resume || "";
    const savedResumeName =
      profileData?.resumeName || userData?.resumeName || "Current resume";
    setResumeUrl(savedResumeUrl);
    setResumeName(savedResumeName);
  }, [profileData, userData]);

  const tier = subscriptionData?.tier || "freemium";
  const creditsRemaining = subscriptionData?.credits_remaining || 0;
  const isProHunterActual = tier === "pro_hunter";
  const isProHunter = TEST_FLAGS.enabled && TEST_FLAGS.forceIsProHunter !== null
    ? TEST_FLAGS.forceIsProHunter
    : isProHunterActual;
  const isSprintPack = tier === "sprint_pack";
  const isPayPerScan = tier === "pay_per_scan";
  const isFreemiumTier = tier === "freemium";
  const isCreditBased = isSprintPack || isPayPerScan;
  const hasUsableCredits = isProHunter || creditsRemaining > 0;
  const canBuySingleScan = !isProHunter && !isSprintPack;
  const hasActiveCampaignActual = Boolean(campaign && !campaign.already_claimed);
  const hasActiveCampaign =
    TEST_FLAGS.enabled && TEST_FLAGS.forceShowCampaignBanner !== null
      ? TEST_FLAGS.forceShowCampaignBanner
      : hasActiveCampaignActual;
  const analysisReasoning = isProHunter ? "high" : isSprintPack ? "medium" : "low";
  const featureToggles = {
    with_ats: isSprintPack || isProHunter,
    with_rewrite_suggestion: isProHunter,
    with_keywords: isSprintPack || isProHunter,
    with_recruiter_insights: isSprintPack || isProHunter,
    is_free_user: isFreemiumTier,
  };
  const activeCampaignData = campaign || {
    name: "Early Bird Promo",
    description: "Get 3 free Job Match Score scans!",
    credits: 3,
  };

  const payPerScanPlan = plans?.find((p) => p.tier === "pay_per_scan");
  const isNigerian = userData?.country == "160";
  const currencySymbol = isNigerian ? "\u20A6" : "$";
  const scanCost = payPerScanPlan
    ? isNigerian
      ? Number(payPerScanPlan.price_ngn || 1000)
      : Number(payPerScanPlan.price_usd || 2.99)
    : isNigerian
      ? 1000
      : 2.99;
  const singleScanPriceDisplay = `${currencySymbol}${numberWithCommas(scanCost.toFixed(2))}`;

  const triggerResumePicker = () => {
    if (!uploadingResume) {
      if (resumeInputRef.current) {
        resumeInputRef.current.click();
      }
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      cogoToast.error("Please upload a PDF or DOC/DOCX resume.");
      return;
    }

    setUploadingResume(true);
    try {
      const uploadResult = await uploadFile(
        file,
        "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload",
      );
      if (!uploadResult?.success || !uploadResult?.data) {
        throw new Error("Upload failed");
      }

      const nextResumeUrl = uploadResult.data;
      const nextResumeName = file.name || uploadResult.fileName || "Resume";
      const mergedProfile = {
        first_name: profileData?.first_name || userData?.first_name || "",
        last_name: profileData?.last_name || userData?.last_name || "",
        title: profileData?.title || userData?.title || "",
        gender: profileData?.gender || userData?.gender || "",
        dob: profileData?.dob || userData?.dob || "",
        state: profileData?.state || userData?.state || "",
        country: profileData?.country || userData?.country || "",
        industry: profileData?.industry || userData?.industry || "",
        image_url: profileData?.image_url || userData?.image_url || "",
        phone: profileData?.phone || userData?.phone || "",
        links: profileData?.links || userData?.links || [],
        about: profileData?.about || userData?.about || "",
        location: profileData?.location || userData?.location || "",
        isShowOnProfile: 1,
        resume: nextResumeUrl,
        resumeName: nextResumeName,
      };

      await httpRequest("post", "/settings/profile/update", mergedProfile);
      await getProfileAction(dispatch);

      setResumeUrl(nextResumeUrl);
      setResumeName(nextResumeName);
      cogoToast.success("Resume updated successfully.");
    } catch (uploadErr) {
      console.error("Resume upload error:", uploadErr);
      cogoToast.error("Unable to upload resume. Please try again.");
    } finally {
      setUploadingResume(false);
    }
  };

  // Pro Hunter users see no upsell

  const runAnalyze = async (submittedUrl, submittedDescription, currentTab) => {
    setAnalyzing(true);
    setError(null);
    setMatchResults(null);

    try {
      const payload = {
        user_id: userData.id,
        include_culture_fit: false,
        with_ats: featureToggles.with_ats,
        with_rewrite_suggestion: featureToggles.with_rewrite_suggestion,
        with_keywords: featureToggles.with_keywords,
        with_recruiter_insights: featureToggles.with_recruiter_insights,
        is_free_user: TEST_FLAGS.enabled && TEST_FLAGS.forceIsFreeForApi !== null
          ? TEST_FLAGS.forceIsFreeForApi
          : featureToggles.is_free_user,
        reasoning: analysisReasoning,
      };

      if (currentTab === "url") {
        payload.job_url = submittedUrl;
      } else {
        payload.job_description = submittedDescription;
      }

      const matchScoreApiUrl =
        process.env.REACT_APP_MATCH_SCORE_API_BASE_URL ||
        "https://jobmatch.workbrook.com";
      const response = await axios.post(
        `${matchScoreApiUrl}/api/v1/get-match-score-external`,
        payload,
      );

      if (response.data) {
        setMatchResults(response.data);
        setJobDescription("");

        let creditConsumed = false;
        if (isCreditBased) {
          try {
            await httpRequest(
              "post",
              "/job-seeker-subscriptions/use-credit",
              {
                feature: "job_match_score",
                job_id: null,
                metadata: {
                  job_url: submittedUrl || null,
                  has_description: !!submittedDescription,
                },
              },
            );
            creditConsumed = true;
            await refreshSubscription();
          } catch (creditErr) {
            console.error("Failed to deduct credit:", creditErr);
          }
        }

        try {
          const saveResult = await saveMatchScoreAction(dispatch, {
            source_type: currentTab === "url" ? "url" : "description",
            job_url: currentTab === "url" ? submittedUrl : null,
            job_description:
              currentTab === "description" ? submittedDescription : null,
            job_title: response.data?.job_title || response.data?.job_info?.job_title || null,
            company_name:
              response.data?.company_name || response.data?.job_info?.hiring_corporation || null,
            match_score: response.data?.match_score ?? 0,
            recommendation: response.data?.recommendation || null,
            matched_skills: response.data?.matched_skills || [],
            missing_skills: response.data?.missing_skills || [],
            culture_fit: response.data?.culture_fit || {},
            details: response.data?.details || {},
            raw_response: response.data,
            credits_consumed: creditConsumed,
          });

          if (saveResult?.type === "SAVE_MATCH_SCORE" && historyFetched) {
            const newRecord = {
              id: saveResult.payload?.id,
              source_type: currentTab === "url" ? "url" : "description",
              job_url: currentTab === "url" ? submittedUrl : null,
              job_description:
                currentTab === "description" ? submittedDescription : null,
              job_title:
                response.data?.job_title || response.data?.job_info?.job_title || null,
              company_name:
                response.data?.company_name ||
                response.data?.job_info?.hiring_corporation ||
                null,
              match_score: response.data?.match_score ?? 0,
              recommendation: response.data?.recommendation || null,
              matched_skills: response.data?.matched_skills || [],
              missing_skills: response.data?.missing_skills || [],
              culture_fit: response.data?.culture_fit || {},
              details: response.data?.details || {},
              raw_response: response.data,
              credits_consumed: creditConsumed ? 1 : 0,
              date_created: new Date().toISOString(),
            };
            setHistoryRecords((prev) => [newRecord, ...prev]);
          }
        } catch (saveErr) {
          console.error("Failed to save match score history:", saveErr);
        }
      }
    } catch (err) {
      console.error("Match score error:", err);
      setError(
        err.response?.data?.detail?.[0]?.msg ||
          "Failed to analyze job match. Please try again.",
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobUrl && !jobDescription) {
      setError("Please enter a job URL or paste a job description");
      return;
    }

    await runAnalyze(jobUrl, jobDescription, activeTab);
  };

  const toggleHistory = async () => {
    const nextOpen = !historyOpen;
    setHistoryOpen(nextOpen);
    if (nextOpen && !historyFetched) {
      setHistoryLoading(true);
      try {
        const result = await getMatchScoreHistoryAction(dispatch, 1, 20);
        if (result?.type === "GET_MATCH_SCORE_HISTORY") {
          setHistoryRecords(result.payload?.data || []);
          setHistoryFetched(true);
        }
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const viewHistoryRecord = (record) => {
    const rehydrated = record?.raw_response || {
      match_score: record?.match_score,
      recommendation: record?.recommendation,
      matched_skills: record?.matched_skills,
      missing_skills: record?.missing_skills,
      culture_fit: record?.culture_fit,
      details: record?.details,
    };
    setMatchResults(rehydrated);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBuyCredit = async () => {
    setBuyingCredit(true);
    try {
      const result = await buyScanCreditsAction(dispatch, {
        quantity: 1,
      });

      if (result?.type === "ERROR") {
        cogoToast.error(
          typeof result.payload === "string"
            ? result.payload
            : "Purchase failed. Please try again.",
        );
        return;
      }

      const payload = result?.payload;

      if (payload?.status) {
        cogoToast.success("Scan credit added to your account.");
        setShowBuyCreditModal(false);
        await refreshSubscription();

        if (pendingAnalyzeAfterBuy && (jobUrl || jobDescription)) {
          setPendingAnalyzeAfterBuy(false);
          await runAnalyze(jobUrl, jobDescription, activeTab);
        }
      } else if (payload?.code === "insufficient_balance") {
        cogoToast.error(
          payload?.message || "Insufficient wallet balance. Please fund your wallet.",
        );
      } else {
        cogoToast.error(payload?.message || "Purchase failed.");
      }
    } catch (err) {
      console.error("Buy credit error:", err);
      cogoToast.error("Unable to complete purchase. Please try again.");
    } finally {
      setBuyingCredit(false);
    }
  };

  const handleClaimCampaign = async () => {
    if (!campaign?.code) return;
    setClaimingCampaign(true);
    try {
      const result = await claimCampaignCreditsAction(dispatch, campaign.code);
      if (result?.payload?.status) {
        cogoToast.success(result.payload.message || "Free credits claimed!");
        setCampaign((prev) => prev ? { ...prev, already_claimed: true } : null);
        await refreshSubscription();
      } else {
        cogoToast.error(
          typeof result?.payload === "string"
            ? result.payload
            : result?.payload?.message || "Could not claim credits.",
        );
      }
    } catch (err) {
      console.error("Campaign claim error:", err);
      cogoToast.error("Unable to claim credits. Please try again.");
    } finally {
      setClaimingCampaign(false);
    }
  };

  const formatHistoryDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (e) {
      return value;
    }
  };

  // Show loading state while fetching subscription
  if (loading) {
    return (
      <Layout>
        <div className="jms-loading-container">
          <CircularProgress size={60} style={{ color: "#1d4ed8" }} />
          <p className="jms-loading-text">Loading your subscription details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="jms-wrapper">
        <div className="jms-page">

        {/* ── Header ── */}
        <div className="jms-header">
          <h2 className="jms-title">Job Match Score</h2>
          <p className="jms-subtitle">
            Complete Step 1 (upload resume), then Step 2 (paste URL or job description).
          </p>
          <p className="jms-pricing">
            {isProHunter ? (
              <>
                <strong style={{ color: "#1d4ed8" }}>Pro Hunter</strong> — Unlimited scans active!
              </>
            ) : creditsRemaining > 0 ? (
              <>
                You have <strong>{creditsRemaining}</strong> scan credit{creditsRemaining === 1 ? "" : "s"} remaining.{" "}
                {!isSprintPack && (
                  <button
                    type="button"
                    className="jms-link-btn"
                    onClick={() => setShowBuyCreditModal(true)}
                  >
                    Buy more
                  </button>
                )}
              </>
            ) : (
              <>
                {!isSprintPack ? (
                  <>
                    <button
                      type="button"
                      className="jms-link-btn"
                      onClick={() => setShowBuyCreditModal(true)}
                    >
                      Buy 1 scan for {singleScanPriceDisplay}
                    </button>
                    {" "}to get started.
                  </>
                ) : (
                  <>You have <strong>0</strong> scan credits remaining.</>
                )}
              </>
            )}
          </p>
        </div>

        {/* ── Promo Banner (single-card mode) ── */}
        {hasActiveCampaign ? (
          <div className="jms-campaign-banner">
            <div className="jms-campaign-left">
              <span className="jms-campaign-emoji">🎁</span>
              <div className="jms-campaign-text">
                <span className="jms-campaign-name">{activeCampaignData.name}</span>
                <span className="jms-campaign-desc">
                  {activeCampaignData.description ||
                    `Claim ${activeCampaignData.credits} free scan credits!`}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="jms-campaign-claim-btn"
              onClick={TEST_FLAGS.enabled ? undefined : handleClaimCampaign}
              disabled={claimingCampaign || (TEST_FLAGS.enabled && !campaign?.code)}
            >
              {claimingCampaign
                ? "Claiming..."
                : `Claim ${activeCampaignData.credits} Free Scans`}
            </button>
          </div>
        ) : !isProHunter ? (
          <div className="jms-limited-plan-banner">
            <div className="jms-limited-plan-left">
              <span className="jms-limited-plan-icon">⚠️</span>
              <div className="jms-limited-plan-text">
                <span className="jms-limited-plan-title">
                  {isSprintPack
                    ? "You're on the Sprint Pack"
                    : isPayPerScan
                    ? "You're on the Pay-Per-Scan Plan"
                    : "You're on the Free Plan"}
                </span>
                <span className="jms-limited-plan-desc">
                  You have <strong>{creditsRemaining}</strong> {isSprintPack || isPayPerScan ? "" : "free "}Advanced Model scan
                  {creditsRemaining === 1 ? "" : "s"} remaining.
                </span>
              </div>
            </div>
            <button
              type="button"
              className="jms-limited-plan-btn"
              onClick={() => history.push("/my-subscriptions")}
            >
              Upgrade to Pro
            </button>
          </div>
        ) : null}

        {/* ── Step 1: Upload Resume ── */}
        <div className="jms-step-heading">
          <span className="jms-step-badge">Step 1</span>
          <span className="jms-step-title">Upload your resume</span>
        </div>
        <div className="jms-resume-cta jms-resume-cta-static">
          <div className="jms-resume-cta-content">
            <div className="jms-resume-cta-left">
              <div className="jms-resume-cta-icon">
                <DescriptionIcon style={{ fontSize: 20 }} />
              </div>
              <div className="jms-resume-cta-text">
                <span className="jms-resume-cta-title">Keep your resume up to date</span>
                <span className="jms-resume-cta-sub">
                  Upload directly here for more accurate ATS and match insights.
                </span>
              </div>
            </div>
            <div className="jms-resume-inline">
              {resumeUrl ? (
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jms-resume-current"
                >
                  Current resume: <strong>{resumeName || "Resume"}</strong>
                </a>
              ) : (
                <span className="jms-resume-current-empty">
                  No resume uploaded yet.
                </span>
              )}
              <input
                ref={resumeInputRef}
                type="file"
                accept="application/pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={handleResumeUpload}
              />
              <button
                type="button"
                className="jms-resume-upload-btn"
                onClick={triggerResumePicker}
                disabled={uploadingResume}
              >
                {uploadingResume ? "Uploading..." : resumeUrl ? "Update Resume" : "Upload Resume"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Buy Scan Credit CTA ── */}
        {canBuySingleScan && (
          <button
            type="button"
            className="jms-buy-credit-btn"
            onClick={() => setShowBuyCreditModal(true)}
            disabled={buyingCredit}
          >
            <FlashOnIcon style={{ fontSize: 17, marginRight: 6 }} />
            {creditsRemaining > 0
              ? `Top up 1 scan credit — ${singleScanPriceDisplay}`
              : `Buy 1 Scan — ${singleScanPriceDisplay}`}
          </button>
        )}

        {/* ── Step 2: Choose Input Method ── */}
        <div className="jms-step-heading">
          <span className="jms-step-badge">Step 2</span>
          <span className="jms-step-title">Choose input method and run analysis</span>
        </div>

        {/* ── Tabs + Input Card ── */}
        <div className="jms-card">
          <div className="jms-custom-tabs">
            <div
              className={`jms-custom-tab ${activeTab === "description" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              <span className="jms-custom-tab-label">
                Paste Job Description
                <span className="jms-tab-badge">Recommended</span>
              </span>
            </div>
            <div
              className={`jms-custom-tab ${activeTab === "url" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("url")}
            >
              Paste Job URL
            </div>
          </div>

          <div className="jms-input-wrap">
            <label className="jms-input-label">
              {activeTab === "url" ? "ENTER JOB LINK" : "PASTE JOB DESCRIPTION"}
            </label>
            {activeTab === "url" ? (
              <input
                type="url"
                className="jms-url-input"
                placeholder="https://www.linkedin.com/jobs/view/..."
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                disabled={analyzing}
              />
            ) : (
              <textarea
                className="jms-textarea"
                placeholder="Paste the full job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
                disabled={analyzing}
              />
            )}
          </div>

          {error && (
            <div className="jms-error-message">
              {error}
            </div>
          )}

          <button 
            className="jms-analyze-btn" 
            onClick={handleAnalyze}
            disabled={analyzing || (!jobUrl && !jobDescription)}
          >
            {analyzing ? (
              <>
                <CircularProgress size={17} style={{ color: "#fff", marginRight: 8 }} />
                Analyzing...
              </>
            ) : (
              <>
                <TrackChangesIcon style={{ fontSize: 17, marginRight: 8 }} />
                Match and Analyze
              </>
            )}
          </button>
        </div>

        {/* ── Match Results ── */}
        {matchResults && (
          <div className="jms-results-container">
            <div className="jms-results-header">
              <h3>Match Analysis Results</h3>
            </div>
            
            {/* Top Row: Match Score Circle & Job Info */}
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
              {/* Match Score Circle */}
              <div className="jms-score-circle" style={{ marginBottom: 0, flexShrink: 0 }}>
                <div className="jms-circle-wrapper">
                  <svg className="jms-circle-svg" viewBox="0 0 120 120">
                    <circle
                      className="jms-circle-bg"
                      cx="60"
                      cy="60"
                      r="54"
                    />
                    <circle
                      className="jms-circle-progress"
                      cx="60"
                      cy="60"
                      r="54"
                      style={{
                        strokeDasharray: `${(matchResults.match_score / 100) * 339.292} 339.292`,
                      }}
                    />
                  </svg>
                  <div className="jms-score-text">
                    <span className="jms-score-number">{matchResults.match_score}%</span>
                    <span className="jms-score-label">Match Score</span>
                  </div>
                </div>
              </div>

              {/* Job Information */}
              {matchResults.job_info && (
                <div className="jms-job-info" style={{ marginBottom: 0, flex: 1, minWidth: '250px' }}>
                  <h4 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: '14px', fontWeight: 700 }}>Job Information</h4>
                  <div className="jms-job-info-grid">
                    <div>
                      <span className="jms-job-info-label">Title</span>
                      <p>{matchResults.job_info.job_title || "N/A"}</p>
                    </div>
                    <div>
                      <span className="jms-job-info-label">Company</span>
                      <p>{matchResults.job_info.hiring_corporation || "N/A"}</p>
                    </div>
                    <div>
                      <span className="jms-job-info-label">Location</span>
                      <p>{matchResults.job_info.job_location || "N/A"}</p>
                    </div>
                    <div>
                      <span className="jms-job-info-label">Salary</span>
                      <p>{matchResults.job_info.salary || "N/A"}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recommendation */}
            <div className="jms-recommendation">
              <h4>Recommendation</h4>
              <p>{matchResults.recommendation}</p>
            </div>

            {/* Skills Section */}
            {(matchResults.matched_skills?.length > 0 || matchResults.missing_skills?.length > 0) && (
              <div className="jms-skills-section">
                {matchResults.matched_skills?.length > 0 && (
                  <div className="jms-skills-matched">
                    <h4>✓ Matched Skills ({matchResults.matched_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.matched_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-matched">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {matchResults.missing_skills?.length > 0 && (
                  <div className="jms-skills-missing">
                    <h4>⚠ Missing Skills ({matchResults.missing_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.missing_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-missing">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ATS + Recruiter Insights */}
            {matchResults.ats_analysis && (
              <div className="jms-ats-panel">
                <div className="jms-ats-header" style={{ cursor: 'pointer', marginBottom: atsOpen ? '16px' : '0' }} onClick={() => setAtsOpen(!atsOpen)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h4 style={{ margin: 0 }}>ATS Analysis</h4>
                    <span className="jms-ats-score">
                      {`${matchResults.ats_analysis.overall_ats_score ?? 0}/100 ATS`} 
                    </span>
                  </div>
                  {atsOpen ? <ExpandLessIcon style={{ color: '#f8fafc' }} /> : <ExpandMoreIcon style={{ color: '#f8fafc' }} />}
                </div>
                
                {atsOpen && (
                  <>
                    <div className="jms-ats-metrics">
                      <div className="jms-ats-metric">
                        <span className="jms-ats-metric-label">Parse confidence</span>
                        <span className="jms-ats-metric-value">
                          {matchResults.ats_analysis.parse_confidence || "N/A"}
                        </span>
                      </div>
                      <div className="jms-ats-metric">
                        <span className="jms-ats-metric-label">Keyword match rate</span>
                        <span className="jms-ats-metric-value">
                          {matchResults.ats_analysis.keyword_match_rate ?? 0}%
                        </span>
                      </div>
                    </div>

                    {Array.isArray(matchResults.ats_analysis.issues) &&
                      matchResults.ats_analysis.issues.length > 0 && (
                        <div className="jms-ats-issues">
                          <h5>ATS Issues</h5>
                          {matchResults.ats_analysis.issues.map((issue, idx) => (
                            <div className="jms-ats-issue" key={idx}>
                              <div className="jms-ats-issue-head">
                                <span>{issue.category || "Issue"}</span>
                                <span className={`jms-ats-severity ${String(issue.severity || "").toLowerCase()}`}>
                                  {issue.severity || "N/A"}
                                </span>
                              </div>
                              <p>{issue.description}</p>
                              {issue.recommendation && (
                                <small>Recommendation: {issue.recommendation}</small>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                    {Array.isArray(matchResults.ats_analysis.top_recommendations) &&
                      matchResults.ats_analysis.top_recommendations.length > 0 && (
                        <div className="jms-ats-top-recs">
                          <h5>Top Recommendations</h5>
                          <ul>
                            {matchResults.ats_analysis.top_recommendations.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </>
                )}
              </div>
            )}

            {/* Details */}
            {matchResults.details && Object.keys(matchResults.details).length > 0 && (
              <div className="jms-details">
                <h4>📋 Detailed Breakdown</h4>
                <div className="jms-details-grid">
                  {Object.entries(matchResults.details).map(([key, value]) => (
                    <div key={key} className="jms-detail-card">
                      <div className="jms-detail-header">
                        <span className="jms-detail-title">{key}</span>
                        {value?.score !== undefined && (
                          <span className="jms-detail-score">
                            {value.score}/{value.max || 100}
                          </span>
                        )}
                      </div>
                      {value?.reason && (
                        <p className="jms-detail-reason">{value.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(matchResults.recruiter_insights || matchResults.ats_analysis?.recruiter_insights) && (
              <div className="jms-recruiter-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: recruiterOpen ? '16px' : '0' }} onClick={() => setRecruiterOpen(!recruiterOpen)}>
                  <h4 style={{ margin: 0 }}>Recruiter Insights</h4>
                  {recruiterOpen ? <ExpandLessIcon style={{ color: '#f8fafc' }} /> : <ExpandMoreIcon style={{ color: '#f8fafc' }} />}
                </div>
                {recruiterOpen && (
                  <>
                    <p>
                      {matchResults.recruiter_insights?.summary ||
                        matchResults.ats_analysis?.recruiter_insights?.summary ||
                        "No summary provided."}
                    </p>
                    <div className="jms-recruiter-breakdown">
                      {(matchResults.recruiter_insights?.breakdown ||
                        matchResults.ats_analysis?.recruiter_insights?.breakdown ||
                        []
                      ).map((item, idx) => (
                        <div className="jms-recruiter-item" key={idx}>
                          <span>{item.dimension}</span>
                          <small>{item.finding}</small>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {(Array.isArray(matchResults.keyword_suggestions) &&
              matchResults.keyword_suggestions.length > 0) ||
            (Array.isArray(matchResults.ats_analysis?.keyword_suggestions) &&
              matchResults.ats_analysis.keyword_suggestions.length > 0) ? (
              <div className="jms-keywords-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: keywordsOpen ? '16px' : '0' }} onClick={() => setKeywordsOpen(!keywordsOpen)}>
                  <h4 style={{ margin: 0 }}>Keyword Suggestions</h4>
                  {keywordsOpen ? <ExpandLessIcon style={{ color: '#f8fafc' }} /> : <ExpandMoreIcon style={{ color: '#f8fafc' }} />}
                </div>
                {keywordsOpen && (
                  <div className="jms-keyword-list">
                    {(matchResults.keyword_suggestions ||
                      matchResults.ats_analysis?.keyword_suggestions ||
                      []
                    ).map((item, idx) => (
                      <div className="jms-keyword-card" key={idx}>
                        <div className="jms-keyword-row">
                          <span className="jms-keyword-pill">{item.keyword}</span>
                          <span className="jms-keyword-priority">{item.priority}</span>
                        </div>
                        {item.context && <p>{item.context}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {Array.isArray(matchResults.ats_analysis?.rewrite_suggestions) &&
              matchResults.ats_analysis.rewrite_suggestions.length > 0 && (
                <div className="jms-rewrite-panel">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: rewriteOpen ? '16px' : '0' }} onClick={() => setRewriteOpen(!rewriteOpen)}>
                    <h4 style={{ margin: 0 }}>AI Rewrite Suggestions</h4>
                    {rewriteOpen ? <ExpandLessIcon style={{ color: '#f8fafc' }} /> : <ExpandMoreIcon style={{ color: '#f8fafc' }} />}
                  </div>
                  {rewriteOpen && (
                    <>
                      {matchResults.ats_analysis.rewrite_suggestions.map((item, idx) => (
                        <div className="jms-rewrite-card" key={idx}>
                          <div className="jms-rewrite-head">
                            <span>{item.section || `Section ${idx + 1}`}</span>
                          </div>
                          <p className="jms-rewrite-original">{item.original}</p>
                          <p className="jms-rewrite-new">{item.rewrite}</p>
                          {item.reason && <small>{item.reason}</small>}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
          </div>
        )}

        {/* ── Previous Scans (collapsible) ── */}
        <div className="jms-history-wrap">
          <button
            type="button"
            className="jms-history-toggle"
            onClick={toggleHistory}
          >
            <span className="jms-history-toggle-left">
              <HistoryIcon style={{ fontSize: 18, marginRight: 8 }} />
              Previous Scans
            </span>
            {historyOpen ? (
              <ExpandLessIcon style={{ fontSize: 20 }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 20 }} />
            )}
          </button>

          {historyOpen && (
            <div className="jms-history-body">
              {historyLoading && (
                <div className="jms-history-loading">
                  <CircularProgress size={22} style={{ color: "#1d4ed8" }} />
                  <span>Loading your previous scans…</span>
                </div>
              )}

              {!historyLoading && historyRecords.length === 0 && (
                <div className="jms-history-empty">
                  You haven&apos;t run any match score analyses yet. Your saved scans will appear here.
                </div>
              )}

              {!historyLoading && historyRecords.length > 0 && (
                <ul className="jms-history-list">
                  {historyRecords.map((record) => {
                    const score = Number(record.match_score || 0);
                    const label =
                      record.job_title ||
                      record.company_name ||
                      (record.source_type === "url"
                        ? record.job_url
                        : "Pasted job description");
                    return (
                      <li key={record.id} className="jms-history-item">
                        <div
                          className={`jms-history-score ${score >= 70 ? "good" : score >= 50 ? "ok" : "low"}`}
                        >
                          {Math.round(score)}%
                        </div>
                        <div className="jms-history-meta">
                          <span className="jms-history-title" title={label}>
                            {label}
                          </span>
                          <span className="jms-history-date">
                            {formatHistoryDate(record.date_created)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="jms-history-view"
                          onClick={() => viewHistoryRecord(record)}
                        >
                          View
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ── Bottom Stats ── */}
        <div className="jms-stats-grid">
          <div className="jms-stat-card jms-stat-amber">
            <div className="jms-stat-icon">
              <EmojiEventsIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-stat-body">
              <span className="jms-stat-label">SCAN CREDITS</span>
              {isProHunter ? (
                <>
                  <p className="jms-stat-value">Unlimited scans</p>
                  <p className="jms-stat-remaining">
                    <strong style={{ color: "#15803d" }}>∞ Unlimited</strong>
                  </p>
                </>
              ) : creditsRemaining > 0 ? (
                <>
                  <p className="jms-stat-value">1 credit per scan</p>
                  <p className="jms-stat-remaining">
                    Credits remaining: <strong>{creditsRemaining}</strong>
                  </p>
                </>
              ) : (
                <>
                  <p className="jms-stat-value">No credits available</p>
                  <p className="jms-stat-remaining">
                    {!isSprintPack ? (
                      <button
                        type="button"
                        className="jms-link-btn"
                        onClick={() => setShowBuyCreditModal(true)}
                      >
                        Buy scan credits
                      </button>
                    ) : (
                      <>Credits remaining: <strong>0</strong></>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        </div>
      </div>

      {/* ── Buy 1 Scan Credit Modal ── */}
      {showBuyCreditModal && (
        <div
          className="jms-modal-overlay"
          onClick={() => {
            if (!buyingCredit) {
              setShowBuyCreditModal(false);
              setPendingAnalyzeAfterBuy(false);
            }
          }}
        >
          <div
            className="jms-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="jms-modal-header">
              <h3 className="jms-modal-title">Buy 1 Scan Credit</h3>
              <button
                type="button"
                className="jms-modal-close"
                onClick={() => {
                  if (!buyingCredit) {
                    setShowBuyCreditModal(false);
                    setPendingAnalyzeAfterBuy(false);
                  }
                }}
                aria-label="Close"
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            <p className="jms-modal-sub">
              Get a single Job Match Score scan for{" "}
              <strong>{singleScanPriceDisplay}</strong>. No subscription required.
              Payment is deducted from your wallet.
            </p>

            {(jobUrl || jobDescription) && (
              <label className="jms-modal-autorun">
                <input
                  type="checkbox"
                  checked={pendingAnalyzeAfterBuy}
                  onChange={(e) => setPendingAnalyzeAfterBuy(e.target.checked)}
                  disabled={buyingCredit}
                />
                <span>
                  Run the match score immediately after purchase
                </span>
              </label>
            )}

            <div className="jms-modal-actions">
              <button
                type="button"
                className="jms-modal-secondary"
                onClick={() => {
                  setShowBuyCreditModal(false);
                  setPendingAnalyzeAfterBuy(false);
                }}
                disabled={buyingCredit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="jms-modal-primary"
                onClick={handleBuyCredit}
                disabled={buyingCredit}
              >
                {buyingCredit ? (
                  <>
                    <CircularProgress
                      size={15}
                      style={{ color: "#fff", marginRight: 8 }}
                    />
                    Processing…
                  </>
                ) : (
                  `Pay ${singleScanPriceDisplay}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JobMatchScore;